import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  Banknote,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  FileCheck2,
  FileUp,
  Landmark,
  Pencil,
  Plus,
  Save,
  Settings2,
  Trash2,
  X
} from "lucide-react";
import {
  DEFAULT_ANNUAL_TAX_RULE,
  annualObligationStatus,
  calculateAnnualTaxObligation,
  defaultDeclarationDate,
  normalizeAnnualTaxRule
} from "../helpers/annualTaxObligation";
import {
  deleteAnnualTaxObligation,
  deleteAnnualTaxDocument,
  saveAnnualTaxObligation,
  saveAnnualTaxRule,
  subscribeAnnualTaxObligations,
  subscribeAnnualTaxRules,
  uploadAnnualTaxDocuments
} from "../services/annualTaxObligationService";

const currentYear = new Date().getFullYear();
const currency = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
  maximumFractionDigits: 2
});
const dateFormatter = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "long",
  year: "numeric"
});

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
};

const emptyForm = (companyOptions = []) => {
  const first = companyOptions[0] || {};
  const fiscalYear = currentYear - 1;
  const taxpayerType = "corporate";
  return {
    companyId: first.id || "",
    companyName: first.name || "",
    companyShortName: first.shortName || first.name || "",
    fiscalYear,
    taxpayerType,
    declarationDate: defaultDeclarationDate({ fiscalYear, taxpayerType }),
    exemptionAmount: "",
    fundDueDateOverride: "",
    ymmDueDateOverride: "",
    fundStatus: "pending",
    ymmStatus: "pending",
    notes: ""
  };
};

const normalizeCompanies = (options = []) => {
  const seen = new Set();
  return options
    .map((item, index) => {
      const name = item.firmaAdi || item.companyName || item.legalName || item.name || "";
      const id = item.id || item.companyId || `company-${index}-${name}`;
      return {
        id,
        name,
        shortName: item.firmaKisaAdi || item.companyShortName || item.shortName || name
      };
    })
    .filter((item) => item.name && !seen.has(item.id) && seen.add(item.id))
    .sort((a, b) => a.name.localeCompare(b.name, "tr"));
};

const STATUS_LABELS = {
  pending: "Bekliyor",
  overdue: "Süresi geçti",
  completed: "Tamamlandı",
  not_applicable: "Gerekli değil"
};

function StatusPill({ required, status, dueDate }) {
  const resolved = annualObligationStatus({ required, status, dueDate });
  return <span className={`annual-status annual-status--${resolved}`}>{STATUS_LABELS[resolved]}</span>;
}

function AnnualTaxObligationsView({
  tenantId,
  user,
  companyOptions = [],
  canEdit = false,
  notifyTeam
}) {
  const companies = useMemo(() => normalizeCompanies(companyOptions), [companyOptions]);
  const [records, setRecords] = useState([]);
  const [rules, setRules] = useState({});
  const [form, setForm] = useState(() => emptyForm(companies));
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [ruleYear, setRuleYear] = useState(currentYear);
  const [ruleForm, setRuleForm] = useState({ ...DEFAULT_ANNUAL_TAX_RULE });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!tenantId) return undefined;
    const unsubscribeRecords = subscribeAnnualTaxObligations({
      tenantId,
      onData: (data) => {
        setRecords(data);
        setLoading(false);
      },
      onError: (subscriptionError) => {
        setError(subscriptionError.message || "Yıllık yükümlülükler alınamadı.");
        setLoading(false);
      }
    });
    const unsubscribeRules = subscribeAnnualTaxRules({
      tenantId,
      onData: setRules,
      onError: (subscriptionError) => setError(subscriptionError.message || "Mevzuat ayarları alınamadı.")
    });
    return () => {
      unsubscribeRecords();
      unsubscribeRules();
    };
  }, [tenantId]);

  useEffect(() => {
    if (!isFormOpen || editingId || form.companyId || companies.length === 0) return;
    setForm(emptyForm(companies));
  }, [companies, editingId, form.companyId, isFormOpen]);

  useEffect(() => {
    setRuleForm(normalizeAnnualTaxRule(rules[String(ruleYear)]));
  }, [ruleYear, rules]);

  const declarationYear = Number(form.fiscalYear || currentYear - 1) + 1;
  const activeRule = normalizeAnnualTaxRule(rules[String(declarationYear)]);
  const calculation = useMemo(() => calculateAnnualTaxObligation({
    ...form,
    exemptionAmount: Number(form.exemptionAmount || 0),
    rule: activeRule
  }), [activeRule, form]);

  const summary = useMemo(() => ({
    companies: new Set(records.map((item) => item.companyId)).size,
    fundTotal: records.reduce((total, item) => total + Number(item.fundAmount || 0), 0),
    pendingFund: records.filter((item) => item.fundRequired && item.fundStatus !== "completed").length,
    pendingYmm: records.filter((item) => item.ymmRequired && item.ymmStatus !== "completed").length
  }), [records]);

  const updateForm = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "companyId") {
        const selected = companies.find((item) => item.id === value);
        next.companyName = selected?.name || "";
        next.companyShortName = selected?.shortName || selected?.name || "";
      }
      if (key === "fiscalYear" || key === "taxpayerType") {
        const fiscalYear = Number(key === "fiscalYear" ? value : current.fiscalYear);
        const taxpayerType = key === "taxpayerType" ? value : current.taxpayerType;
        next.declarationDate = defaultDeclarationDate({
          fiscalYear,
          taxpayerType,
          rule: normalizeAnnualTaxRule(rules[String(fiscalYear + 1)])
        });
      }
      return next;
    });
  };

  const openNew = () => {
    setEditingId("");
    setForm(emptyForm(companies));
    setError("");
    setMessage("");
    setIsFormOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setForm({
      companyId: record.companyId,
      companyName: record.companyName,
      companyShortName: record.companyShortName || record.companyName,
      fiscalYear: record.fiscalYear,
      taxpayerType: record.taxpayerType || "corporate",
      declarationDate: record.declarationDate || "",
      exemptionAmount: record.exemptionAmount ?? "",
      fundDueDateOverride: record.fundDueDateOverride || "",
      ymmDueDateOverride: record.ymmDueDateOverride || "",
      fundStatus: record.fundStatus || "pending",
      ymmStatus: record.ymmStatus || "pending",
      notes: record.notes || ""
    });
    setError("");
    setMessage("");
    setIsFormOpen(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const record = await saveAnnualTaxObligation({
        tenantId,
        form: { ...form, exemptionAmount: Number(form.exemptionAmount || 0) },
        rule: activeRule,
        user
      });
      await notifyTeam?.({
        title: "Yıllık yükümlülük kaydı güncellendi",
        body: `${record.companyShortName || record.companyName} · ${record.fiscalYear} dönemi hesaplandı.`,
        type: "info",
        link: "annualTaxObligations",
        processProjectId: "annual-tax-obligations"
      });
      setMessage("Hesaplama kaydedildi; gerekli aylık süreç görevleri güncellendi.");
      setIsFormOpen(false);
      setEditingId("");
    } catch (saveError) {
      setError(saveError.message || "Kayıt oluşturulamadı.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`${record.companyName} ${record.fiscalYear} kaydı ve bağlı görevler silinsin mi?`)) return;
    try {
      await deleteAnnualTaxObligation({ tenantId, record });
      setMessage("Yıllık yükümlülük kaydı silindi.");
    } catch (deleteError) {
      setError(deleteError.message || "Kayıt silinemedi.");
    }
  };

  const handleRuleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await saveAnnualTaxRule({ tenantId, declarationYear: ruleYear, rule: ruleForm, user });
      setMessage(`${ruleYear} beyan yılı mevzuat ayarları kaydedildi.`);
      setIsRulesOpen(false);
    } catch (saveError) {
      setError(saveError.message || "Mevzuat ayarları kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (record, kind, event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;
    const key = `${record.id}-${kind}`;
    setUploadingKey(key);
    setError("");
    setMessage("");
    try {
      await uploadAnnualTaxDocuments({ tenantId, record, kind, files, user });
      const obligationName = kind === "fund" ? "%3 fon yatırımı" : "YMM tasdik raporu";
      await notifyTeam?.({
        title: `${obligationName} tamamlandı`,
        body: `${record.companyShortName || record.companyName} · ${record.fiscalYear} dönemi belgeleri sisteme yüklendi.`,
        type: "success",
        link: "annualTaxObligations",
        processProjectId: "annual-tax-obligations"
      });
      setMessage(`${obligationName} tamamlandı olarak işaretlendi ve ${files.length} belge kaydedildi.`);
    } catch (uploadError) {
      setError(uploadError.message || "Belge yüklenemedi.");
    } finally {
      setUploadingKey("");
    }
  };

  const handleDocumentDelete = async (record, kind, documentItem) => {
    if (!window.confirm(`${documentItem.name} silinsin mi? Son belge silinirse yükümlülük yeniden bekliyor durumuna alınır.`)) return;
    const key = `${record.id}-${kind}`;
    setUploadingKey(key);
    setError("");
    try {
      await deleteAnnualTaxDocument({ tenantId, record, kind, documentItem, user });
      setMessage("Belge silindi. Gerekliyse aylık görevler yeniden oluşturuldu.");
    } catch (deleteError) {
      setError(deleteError.message || "Belge silinemedi.");
    } finally {
      setUploadingKey("");
    }
  };

  return (
    <section className="annual-tax-view">
      <header className="annual-tax-header">
        <div className="annual-tax-heading">
          <span className="annual-tax-heading__icon"><Landmark size={24} /></span>
          <div>
            <h1>Yıllık Beyan ve Yükümlülükler</h1>
            <p>Kazanç istisnasını kaydedin; %3 fon ve YMM rapor takvimini otomatik yönetin.</p>
          </div>
        </div>
        {canEdit && (
          <div className="annual-tax-header__actions">
            <button type="button" className="annual-button annual-button--secondary" onClick={() => setIsRulesOpen(true)}>
              <Settings2 size={17} /> Mevzuat ayarları
            </button>
            <button type="button" className="annual-button annual-button--primary" onClick={openNew}>
              <Plus size={17} /> Yeni beyan ekle
            </button>
          </div>
        )}
      </header>

      {message && <div className="annual-banner annual-banner--success"><CheckCircle2 size={18} /> {message}</div>}
      {error && <div className="annual-banner annual-banner--error"><AlertTriangle size={18} /> {error}</div>}

      <div className="annual-summary-grid">
        <article><span><Landmark size={18} /></span><small>Kayıtlı firma</small><strong>{summary.companies}</strong></article>
        <article><span><Banknote size={18} /></span><small>Toplam fon yükümlülüğü</small><strong>{currency.format(summary.fundTotal)}</strong></article>
        <article><span><CalendarClock size={18} /></span><small>Bekleyen fon işlemi</small><strong>{summary.pendingFund}</strong></article>
        <article><span><FileCheck2 size={18} /></span><small>Bekleyen YMM raporu</small><strong>{summary.pendingYmm}</strong></article>
      </div>

      {isFormOpen && (
        <form className="annual-panel annual-form" onSubmit={handleSave}>
          <div className="annual-panel__title">
            <div><h2>{editingId ? "Yıllık beyanı düzenle" : "Yeni yıllık beyan"}</h2><p>Hesaplama, girilen beyan yılına ait mevzuat ayarlarıyla yapılır.</p></div>
            <button type="button" className="annual-icon-button" onClick={() => setIsFormOpen(false)} aria-label="Formu kapat"><X size={19} /></button>
          </div>
          <div className="annual-form-grid">
            <label>Firma
              <select value={form.companyId} onChange={(event) => updateForm("companyId", event.target.value)} required disabled={Boolean(editingId)}>
                <option value="">Firma seçin</option>
                {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
              </select>
            </label>
            <label>Kazancın ait olduğu yıl
              <input type="number" min="2000" max={currentYear} value={form.fiscalYear} onChange={(event) => updateForm("fiscalYear", event.target.value)} required disabled={Boolean(editingId)} />
            </label>
            <label>Mükellef türü
              <select value={form.taxpayerType} onChange={(event) => updateForm("taxpayerType", event.target.value)}>
                <option value="corporate">A.Ş. / Ltd. (Kurumlar vergisi)</option>
                <option value="sole">Şahıs firması (Gelir vergisi)</option>
              </select>
            </label>
            <label>Beyan tarihi
              <input type="date" value={form.declarationDate} onChange={(event) => updateForm("declarationDate", event.target.value)} required />
            </label>
            <label className="annual-form-grid__wide">Teknoloji Geliştirme Bölgesinde Kazanç İstisna Tutarı
              <div className="annual-money-input"><span>₺</span><input type="number" min="0" step="0.01" value={form.exemptionAmount} onChange={(event) => updateForm("exemptionAmount", event.target.value)} placeholder="0,00" required /></div>
            </label>
            <label>Fon son tarihi (uzatma varsa)
              <input type="date" value={form.fundDueDateOverride} onChange={(event) => updateForm("fundDueDateOverride", event.target.value)} />
            </label>
            <label>YMM son tarihi (uzatma varsa)
              <input type="date" value={form.ymmDueDateOverride} onChange={(event) => updateForm("ymmDueDateOverride", event.target.value)} />
            </label>
            <label className="annual-form-grid__wide">Notlar<textarea rows="3" value={form.notes} onChange={(event) => updateForm("notes", event.target.value)} placeholder="Beyan veya uzatma kararına ilişkin not ekleyin…" /></label>
          </div>

          <div className="annual-calculation-preview">
            <article className={calculation.fundRequired ? "is-required" : ""}>
              <div><Banknote size={19} /><strong>%3 Fon Yükümlülüğü</strong></div>
              <b>{calculation.fundRequired ? currency.format(calculation.fundAmount) : "Yükümlülük yok"}</b>
              <small>{currency.format(activeRule.fundThreshold)} ve üzeri · Son tarih {formatDate(calculation.fundDueDate)}</small>
            </article>
            <article className={calculation.ymmRequired ? "is-required" : ""}>
              <div><FileCheck2 size={19} /><strong>YMM Rapor Zorunluluğu</strong></div>
              <b>{calculation.ymmRequired ? "Rapor gerekli" : "Rapor gerekli değil"}</b>
              <small>{currency.format(activeRule.ymmThreshold)} üzeri · Son tarih {formatDate(calculation.ymmDueDate)}</small>
            </article>
          </div>
          <div className="annual-form-actions">
            <button type="button" className="annual-button annual-button--secondary" onClick={() => setIsFormOpen(false)}>Vazgeç</button>
            <button type="submit" className="annual-button annual-button--primary" disabled={saving}><Save size={17} /> {saving ? "Kaydediliyor…" : "Kaydet ve görevleri oluştur"}</button>
          </div>
        </form>
      )}

      <div className="annual-panel annual-records">
        <div className="annual-panel__title"><div><h2>Yıllık kayıtlar</h2><p>{records.length} firma-dönem kaydı</p></div></div>
        {loading ? <div className="annual-empty">Kayıtlar yükleniyor…</div> : records.length === 0 ? (
          <div className="annual-empty"><BadgeCheck size={32} /><strong>Henüz yıllık beyan kaydı yok</strong><span>İlk kazanç istisnası kaydını eklediğinizde hesaplamalar burada görünecek.</span></div>
        ) : (
          <div className="annual-record-list">
            {records.map((record) => {
              const fundDocuments = record.fundDocuments || [];
              const ymmDocuments = record.ymmDocuments || [];
              return (
              <article className="annual-record-card" key={record.id}>
                <div className="annual-record-card__company"><span>{record.fiscalYear}</span><div><strong>{record.companyShortName || record.companyName}</strong><small>{record.taxpayerType === "sole" ? "Şahıs firması" : "Kurumlar vergisi mükellefi"} · Beyan {formatDate(record.declarationDate)}</small></div></div>
                <div className="annual-record-card__amount"><small>Kazanç istisnası</small><strong>{currency.format(Number(record.exemptionAmount || 0))}</strong></div>
                <div className="annual-record-card__obligation"><div><small>%3 fon</small><StatusPill required={record.fundRequired} status={record.fundStatus} dueDate={record.fundDueDate} /></div><strong>{record.fundRequired ? currency.format(Number(record.fundAmount || 0)) : "Gerekli değil"}</strong><small>{record.fundRequired ? formatDate(record.fundDueDate) : "Eşik altında"}</small></div>
                <div className="annual-record-card__obligation"><div><small>YMM raporu</small><StatusPill required={record.ymmRequired} status={record.ymmStatus} dueDate={record.ymmDueDate} /></div><strong>{record.ymmRequired ? "Rapor gerekli" : "Gerekli değil"}</strong><small>{record.ymmRequired ? formatDate(record.ymmDueDate) : "Eşik altında"}</small></div>
                {canEdit && <div className="annual-record-card__actions"><button type="button" className="annual-icon-button" onClick={() => openEdit(record)} aria-label="Düzenle"><Pencil size={17} /></button><button type="button" className="annual-icon-button annual-icon-button--danger" onClick={() => handleDelete(record)} aria-label="Sil"><Trash2 size={17} /></button></div>}
                <div className="annual-record-card__documents">
                  {record.fundRequired && (
                    <div className="annual-document-group">
                      <div className="annual-document-group__heading">
                        <div><strong>%3 fon belgeleri</strong><small>{record.fundStatus === "completed" ? `${formatDate(record.fundCompletedAt)} · ${record.fundCompletedByName || "Tamamlandı"}` : "Belge yüklenince tamamlandı olarak işaretlenir."}</small></div>
                        {canEdit && <label className={`annual-document-upload ${uploadingKey === `${record.id}-fund` ? "is-loading" : ""}`}><FileUp size={16} /> {uploadingKey === `${record.id}-fund` ? "Yükleniyor…" : fundDocuments.length ? "Belge ekle" : "Belge yükle ve tamamla"}<input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.docx" disabled={Boolean(uploadingKey)} onChange={(event) => handleDocumentUpload(record, "fund", event)} /></label>}
                      </div>
                      {fundDocuments.length > 0 && <div className="annual-document-list">{fundDocuments.map((item) => <span className="annual-document-chip" key={item.path}><a href={item.url} target="_blank" rel="noreferrer"><ExternalLink size={13} /> {item.name}</a>{canEdit && <button type="button" onClick={() => handleDocumentDelete(record, "fund", item)} aria-label={`${item.name} belgesini sil`}><X size={13} /></button>}</span>)}</div>}
                    </div>
                  )}
                  {record.ymmRequired && (
                    <div className="annual-document-group">
                      <div className="annual-document-group__heading">
                        <div><strong>YMM raporu belgeleri</strong><small>{record.ymmStatus === "completed" ? `${formatDate(record.ymmCompletedAt)} · ${record.ymmCompletedByName || "Tamamlandı"}` : "Rapor yüklenince tamamlandı olarak işaretlenir."}</small></div>
                        {canEdit && <label className={`annual-document-upload ${uploadingKey === `${record.id}-ymm` ? "is-loading" : ""}`}><FileUp size={16} /> {uploadingKey === `${record.id}-ymm` ? "Yükleniyor…" : ymmDocuments.length ? "Belge ekle" : "Rapor yükle ve tamamla"}<input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.docx" disabled={Boolean(uploadingKey)} onChange={(event) => handleDocumentUpload(record, "ymm", event)} /></label>}
                      </div>
                      {ymmDocuments.length > 0 && <div className="annual-document-list">{ymmDocuments.map((item) => <span className="annual-document-chip" key={item.path}><a href={item.url} target="_blank" rel="noreferrer"><ExternalLink size={13} /> {item.name}</a>{canEdit && <button type="button" onClick={() => handleDocumentDelete(record, "ymm", item)} aria-label={`${item.name} belgesini sil`}><X size={13} /></button>}</span>)}</div>}
                    </div>
                  )}
                </div>
              </article>
            );})}
          </div>
        )}
      </div>

      {isRulesOpen && (
        <div className="annual-modal-backdrop" role="presentation">
          <form className="annual-modal" onSubmit={handleRuleSave}>
            <div className="annual-panel__title"><div><h2>Mevzuat ayarları</h2><p>Tebliğ ve karar değişikliklerini beyan yılı bazında yönetin.</p></div><button type="button" className="annual-icon-button" onClick={() => setIsRulesOpen(false)} aria-label="Kapat"><X size={19} /></button></div>
            <div className="annual-form-grid">
              <label>Beyan yılı<input type="number" min="2020" max="2100" value={ruleYear} onChange={(event) => setRuleYear(Number(event.target.value))} /></label>
              <label>Fon bildirim başlangıç ayı<input type="number" min="1" max="12" value={ruleForm.fundReminderStartMonth} onChange={(event) => setRuleForm((current) => ({ ...current, fundReminderStartMonth: Number(event.target.value) }))} /></label>
              <label>Fon eşiği (₺)<input type="number" min="0" value={ruleForm.fundThreshold} onChange={(event) => setRuleForm((current) => ({ ...current, fundThreshold: Number(event.target.value) }))} /></label>
              <label>Fon oranı (%)<input type="number" min="0" step="0.01" value={Number(ruleForm.fundRate || 0) * 100} onChange={(event) => setRuleForm((current) => ({ ...current, fundRate: Number(event.target.value) / 100 }))} /></label>
              <label>Azami fon tutarı (₺)<input type="number" min="0" value={ruleForm.fundCap} onChange={(event) => setRuleForm((current) => ({ ...current, fundCap: Number(event.target.value) }))} /></label>
              <label>YMM rapor eşiği (₺)<input type="number" min="0" value={ruleForm.ymmThreshold} onChange={(event) => setRuleForm((current) => ({ ...current, ymmThreshold: Number(event.target.value) }))} /></label>
              <label>Şahıs beyan ayı<input type="number" min="1" max="12" value={ruleForm.soleDeclarationMonth} onChange={(event) => setRuleForm((current) => ({ ...current, soleDeclarationMonth: Number(event.target.value) }))} /></label>
              <label>Kurum beyan ayı<input type="number" min="1" max="12" value={ruleForm.corporateDeclarationMonth} onChange={(event) => setRuleForm((current) => ({ ...current, corporateDeclarationMonth: Number(event.target.value) }))} /></label>
              <label>YMM teslim farkı (ay)<input type="number" min="1" max="6" value={ruleForm.ymmDueOffsetMonths} onChange={(event) => setRuleForm((current) => ({ ...current, ymmDueOffsetMonths: Number(event.target.value) }))} /></label>
            </div>
            <div className="annual-form-actions"><button type="button" className="annual-button annual-button--secondary" onClick={() => setRuleForm({ ...DEFAULT_ANNUAL_TAX_RULE })}>Varsayılanı yükle</button><button type="submit" className="annual-button annual-button--primary" disabled={saving}><Save size={17} /> Ayarları kaydet</button></div>
          </form>
        </div>
      )}
    </section>
  );
}

export default AnnualTaxObligationsView;
